'use client';
import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import AppNav from '@/components/AppNav';

interface Message {
  role: 'ai' | 'user';
  content: string;
  options?: string[];
  recommendation?: {
    primary: string;
    alternatives: string[];
    disclaimer: string;
  };
}

const initialMessages: Message[] = [
  {
    role: 'ai',
    content: "Hi! I'm your BusinessOS AI assistant. I'll help you figure out the best way to structure your business in India.\n\nWhat are you planning to start?",
    options: ['A tech company', 'A manufacturing business', 'A services firm', 'A retail business', 'Something else'],
  },
];

export default function OnboardingPage() {
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [businessContext, setBusinessContext] = useState<Record<string, string>>({});
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text: string) => {
    if (!text.trim() || loading) return;

    const userMessage: Message = { role: 'user', content: text };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    const newContext = { ...businessContext };
    if (step === 0) newContext.businessType = text;
    if (step === 1) newContext.founders = text;
    if (step === 2) newContext.investment = text;
    setBusinessContext(newContext);

    try {
      const allMessages = [...messages, userMessage].map(m => ({
        role: m.role === 'ai' ? 'assistant' : 'user',
        content: m.content,
      }));

      const res = await fetch('/api/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'chat', messages: allMessages, businessContext: newContext }),
      });

      const data = await res.json();
      const aiResponse = data.response || "I understand. Let me help you with that.";

      const newStep = step + 1;
      setStep(newStep);

      let aiMessage: Message = { role: 'ai', content: aiResponse };

      // After 3 exchanges, show recommendation
      if (newStep >= 3) {
        aiMessage = {
          role: 'ai',
          content: aiResponse,
          recommendation: {
            primary: 'Private Limited Company',
            alternatives: ['LLP', 'OPC', 'Partnership'],
            disclaimer: 'This is informational guidance only, not legal advice. Please consult a qualified professional before making a decision.',
          },
        };
      } else if (newStep === 1) {
        aiMessage.options = ['Just me', '2 founders', '3-4 founders', '5+ founders'];
      } else if (newStep === 2) {
        aiMessage.options = ['Yes, planning to raise funding', 'Maybe in the future', 'No, bootstrapped'];
      }

      setMessages(prev => [...prev, aiMessage]);
    } catch (err) {
      console.error('AI error:', err);
      const fallbackMessages: Record<number, Message> = {
        1: { role: 'ai', content: 'How many founders will be involved in this business?', options: ['Just me', '2 founders', '3-4 founders', '5+ founders'] },
        2: { role: 'ai', content: 'Are you planning to raise investment or funding?', options: ['Yes, planning to raise funding', 'Maybe in the future', 'No, bootstrapped'] },
        3: {
          role: 'ai',
          content: 'Based on your answers, a Private Limited Company may be suitable for your business. It offers limited liability, easy fundraising, and credibility.',
          recommendation: {
            primary: 'Private Limited Company',
            alternatives: ['LLP', 'OPC', 'Partnership'],
            disclaimer: 'This is informational guidance only, not legal advice. Please consult a qualified professional.',
          },
        },
      };
      setMessages(prev => [...prev, fallbackMessages[step + 1] || { role: 'ai', content: 'Let me help you further. What else would you like to know?' }]);
      setStep(prev => prev + 1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-2 font-sans">
      <AppNav variant="app" activePath="/onboarding" />

      <main className="sm:ml-60 pt-14 sm:pt-0 min-h-screen flex flex-col">
        <div className="max-w-3xl mx-auto w-full px-6 py-8 flex-1 flex flex-col">

          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 bg-ai rounded-lg flex items-center justify-center">
                <span className="text-white text-xs font-bold">AI</span>
              </div>
              <h1 className="text-xl font-bold text-ink">AI Business Onboarding</h1>
            </div>
            <p className="text-[13px] text-ink-muted">Tell me about your business. I&apos;ll recommend the best structure.</p>
          </div>

          {/* Chat */}
          <div className="flex-1 bg-white rounded-2xl border border-border overflow-hidden flex flex-col">
            <div className="flex-1 overflow-y-auto p-6 space-y-5 max-h-[500px]">
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] ${msg.role === 'user' ? 'order-2' : 'order-1'}`}>
                    {msg.role === 'ai' && (
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 bg-ai rounded-md flex items-center justify-center">
                          <span className="text-white text-[10px] font-bold">AI</span>
                        </div>
                        <span className="text-[11px] font-semibold text-ai">BusinessOS AI</span>
                      </div>
                    )}

                    <div className={`rounded-2xl px-4 py-3 text-[14px] leading-relaxed whitespace-pre-wrap ${
                      msg.role === 'user' ?'bg-ai text-white rounded-tr-sm' :'bg-surface-2 text-ink rounded-tl-sm border border-border'
                    }`}>
                      {msg.content}
                    </div>

                    {/* Quick options */}
                    {msg.options && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {msg.options.map(opt => (
                          <button
                            key={opt}
                            onClick={() => sendMessage(opt)}
                            className="px-3 py-1.5 bg-white border border-border rounded-lg text-[12px] font-medium text-ink-muted hover:text-ai hover:border-ai/30 transition-all"
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Recommendation Card */}
                    {msg.recommendation && (
                      <div className="mt-4 bg-white border border-border rounded-xl overflow-hidden">
                        <div className="bg-ai-light border-b border-ai/20 px-4 py-3">
                          <p className="text-[11px] font-semibold text-ai uppercase tracking-wide">AI Recommendation</p>
                        </div>
                        <div className="p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-[11px] font-semibold bg-ai text-white px-2 py-0.5 rounded-full">Recommended</span>
                            <span className="font-semibold text-ink text-[15px]">{msg.recommendation.primary}</span>
                          </div>
                          <div className="flex gap-2 mb-4">
                            {msg.recommendation.alternatives.map(alt => (
                              <span key={alt} className="text-[11px] font-medium bg-surface-3 text-ink-muted px-2 py-1 rounded-lg">{alt}</span>
                            ))}
                          </div>
                          <p className="text-[11px] text-ink-muted italic border-t border-border pt-3">{msg.recommendation.disclaimer}</p>
                          <div className="mt-4 flex gap-3">
                            <Link href="/onboarding/company" className="flex-1 text-center bg-ai text-white text-[13px] font-semibold py-2.5 rounded-lg hover:bg-ai-dark transition-colors">
                              Start Company Registration →
                            </Link>
                            <Link href="/business" className="px-4 py-2.5 border border-border rounded-lg text-[13px] font-medium text-ink-muted hover:text-ink transition-colors">
                              Save & Continue Later
                            </Link>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="bg-surface-2 border border-border rounded-2xl rounded-tl-sm px-4 py-3">
                    <div className="flex gap-1">
                      <span className="w-1.5 h-1.5 bg-ai rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                      <span className="w-1.5 h-1.5 bg-ai rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                      <span className="w-1.5 h-1.5 bg-ai rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="border-t border-border p-4">
              <div className="flex gap-3">
                <input
                  type="text"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && sendMessage(input)}
                  placeholder="Type your answer..."
                  className="flex-1 bg-surface-2 border border-border rounded-xl px-4 py-2.5 text-[14px] text-ink placeholder-ink-subtle focus:outline-none focus:ring-2 focus:ring-ai/20 focus:border-ai/40 transition-all"
                />
                <button
                  onClick={() => sendMessage(input)}
                  disabled={loading || !input.trim()}
                  className="bg-ai text-white px-4 py-2.5 rounded-xl font-medium text-[14px] hover:bg-ai-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
